import React, { useCallback, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { Box, Chip, Collapse, Paper, Stack, Typography } from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import EastIcon from "@mui/icons-material/East";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const labelStyle = {
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.3px",
  textTransform: "uppercase",
  fontFamily: "Outfit, sans-serif",
};

function FlowBadge({ label, tone = "neutral" }) {
  const toneMap = {
    neutral: { bg: "#F2F5FA", color: "#5F6B84" },
    primary: { bg: "#E7F0FD", color: "#0A65E4" },
    success: { bg: "#EAF7EE", color: "#237B4B" },
    warning: { bg: "#FFF4DE", color: "#B76E00" },
    danger: { bg: "#FDECEC", color: "#D14343" },
  };

  const colors = toneMap[tone] || toneMap.neutral;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: "22px",
        bgcolor: colors.bg,
        color: colors.color,
        fontFamily: "Outfit, sans-serif",
        fontWeight: 600,
        fontSize: "11px",
      }}
    />
  );
}

function buildRoundedOrthogonalPath(points, radius = 10) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const next = points[index + 1];

    if (!next) {
      path += ` L ${current.x} ${current.y}`;
      continue;
    }

    const incomingHorizontal = previous.y === current.y;
    const outgoingHorizontal = current.y === next.y;

    const incomingDistance = Math.abs(
      incomingHorizontal ? current.x - previous.x : current.y - previous.y
    );
    const outgoingDistance = Math.abs(
      outgoingHorizontal ? next.x - current.x : next.y - current.y
    );
    const bend = Math.min(radius, incomingDistance / 2, outgoingDistance / 2);

    const entry = incomingHorizontal
      ? {
          x: current.x - Math.sign(current.x - previous.x) * bend,
          y: current.y,
        }
      : {
          x: current.x,
          y: current.y - Math.sign(current.y - previous.y) * bend,
        };

    const exit = outgoingHorizontal
      ? {
          x: current.x + Math.sign(next.x - current.x) * bend,
          y: current.y,
        }
      : {
          x: current.x,
          y: current.y + Math.sign(next.y - current.y) * bend,
        };

    path += ` L ${entry.x} ${entry.y}`;
    path += ` Q ${current.x} ${current.y} ${exit.x} ${exit.y}`;
  }

  return path;
}

function splitTextIntoLines(text, maxCharsPerLine, maxLines) {
  const value = String(text || "").trim();
  if (!value) return [];

  const words = value.split(/\s+/);
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (nextLine.length <= maxCharsPerLine) {
      currentLine = nextLine;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
      currentLine = word;
      return;
    }

    lines.push(word.slice(0, maxCharsPerLine));
    currentLine = word.slice(maxCharsPerLine);
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  if (!maxLines || lines.length <= maxLines) {
    return lines;
  }

  const visibleLines = lines.slice(0, maxLines);
  const lastLine = visibleLines[maxLines - 1];
  visibleLines[maxLines - 1] =
    lastLine.length > maxCharsPerLine - 3
      ? `${lastLine.slice(0, maxCharsPerLine - 3)}...`
      : `${lastLine}...`;

  return visibleLines;
}

function buildChildFlowLayouts(childSteps) {
  const childLineHeight = 12;
  const rawLayouts = (childSteps || []).map((childStep, childIndex) => {
    const childHasDecision =
      childStep.onApproveAction === "gotostep" &&
      childStep.onApproveGoToSequence &&
      childStep.onRejectAction === "gotostep" &&
      childStep.onRejectGoToSequence;
    const isParallelChild = Boolean(childStep.allowParallelExecution);
    const childTitleLines = splitTextIntoLines(
      childStep.childStepName?.trim() || "Untitled Child Step",
      childHasDecision ? 18 : isParallelChild ? 18 : 34,
      childHasDecision ? 2 : isParallelChild ? 4 : 3
    );

    return {
      titleLines: childTitleLines,
      height: childHasDecision
        ? 58 + Math.max(0, childTitleLines.length - 1) * childLineHeight
        : 30 + Math.max(0, childTitleLines.length - 1) * childLineHeight,
      hasDecision: Boolean(childHasDecision),
      approveTargetIndex:
        childStep.onApproveAction === "gotostep" && childStep.onApproveGoToSequence
          ? childStep.onApproveGoToSequence - 1
          : null,
      rejectTargetIndex:
        childStep.onRejectAction === "gotostep" && childStep.onRejectGoToSequence
          ? childStep.onRejectGoToSequence - 1
          : null,
      sequence: childIndex + 1,
      titleY: childHasDecision ? 38 : 15,
      allowParallelExecution: isParallelChild,
      childIndex,
    };
  });

  const childRows = [];
  for (let index = 0; index < rawLayouts.length;) {
    if (rawLayouts[index].allowParallelExecution) {
      let nextIndex = index;
      while (nextIndex < rawLayouts.length && rawLayouts[nextIndex].allowParallelExecution) {
        nextIndex += 1;
      }
      if (nextIndex - index >= 2) {
        childRows.push({
          indices: Array.from({ length: nextIndex - index }, (_, offset) => index + offset),
          isParallel: true,
        });
        index = nextIndex;
        continue;
      }
    }

    childRows.push({
      indices: [index],
      isParallel: false,
    });
    index += 1;
  }

  let childCursorY = 28;
  const hydratedRows = childRows.map((row, rowIndex) => {
    const rowHeight = Math.max(...row.indices.map((childIndex) => rawLayouts[childIndex].height));
    const hydratedRow = {
      ...row,
      rowIndex,
      y: childCursorY,
      height: rowHeight,
    };

    row.indices.forEach((childIndex, position) => {
      rawLayouts[childIndex] = {
        ...rawLayouts[childIndex],
        rowIndex,
        rowY: childCursorY,
        rowHeight,
        rowSize: row.indices.length,
        rowPosition: position,
        isParallelRow: row.isParallel,
      };
    });

    childCursorY += rowHeight;
    if (rowIndex < childRows.length - 1) {
      childCursorY += 18;
    }

    return hydratedRow;
  });

  return {
    childLayouts: rawLayouts,
    childRows: hydratedRows,
    childContainerHeight: rawLayouts.length ? childCursorY + 12 : 0,
  };
}

function getChildLayoutGeometry(node, childNode) {
  const childY = node.y + node.mainHeight + 14 + childNode.rowY;
  const singleRowX = node.x + node.width / 2 - node.childContainerWidth / 2 + 12;
  const singleRowWidth = node.childContainerWidth - 24;

  let childX = singleRowX;
  let childWidth = singleRowWidth;

  if (childNode.rowSize > 1) {
    const rowGap = 18;
    const availableWidth = node.childContainerWidth - 24;
    childWidth = Math.floor((availableWidth - rowGap * (childNode.rowSize - 1)) / childNode.rowSize);
    const totalWidth = childWidth * childNode.rowSize + rowGap * (childNode.rowSize - 1);
    const startX = node.x + node.width / 2 - totalWidth / 2;
    childX = startX + childNode.rowPosition * (childWidth + rowGap);
  } else {
    childWidth = Math.min(singleRowWidth, 280);
    childX = node.x + node.width / 2 - childWidth / 2;
  }

  const childCenterX = childX + childWidth / 2;
  const childShapeWidth = childNode.hasDecision ? Math.min(childWidth - 8, 190) : childWidth;
  const childShapeX = childNode.hasDecision
    ? childX + (childWidth - childShapeWidth) / 2
    : childX;

  return {
    childX,
    childY,
    childWidth,
    childCenterX,
    childShapeWidth,
    childShapeX,
    childMidY: childY + childNode.height / 2,
  };
}

function RoutePill({ label, target, tone }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <FlowBadge label={label} tone={tone} />
      <EastIcon sx={{ fontSize: 16, color: tone === "danger" ? "#D14343" : "#237B4B" }} />
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: "#2C274A",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        {target}
      </Typography>
    </Box>
  );
}

function ChildStepsMiniFlow({ childSteps, getRoleLabel }) {
  if (!childSteps.length) return null;

  return (
    <Box
      sx={{
        mb: 1.3,
        p: 1.2,
        borderRadius: "3px",
        border: "1px solid #E4EAF4",
        bgcolor: "#F8FAFD",
      }}
    >
      <Typography
        sx={{
          ...labelStyle,
          color: "#72809A",
          mb: 1,
        }}
      >
        Child Flow
      </Typography>

      <Stack spacing={0.9}>
        {childSteps.map((childStep, childIndex) => {
          const approveSequence =
            childStep.onApproveAction === "gotostep" && childStep.onApproveGoToSequence
              ? childStep.onApproveGoToSequence
              : childIndex < childSteps.length - 1
                ? childIndex + 2
                : null;
          const rejectSequence =
            childStep.onRejectAction === "gotostep" && childStep.onRejectGoToSequence
              ? childStep.onRejectGoToSequence
              : null;
          const hasDecision = Boolean(approveSequence && rejectSequence);

          return (
            <React.Fragment key={childStep.workflowChildStepId || childIndex}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "3px",
                  border: hasDecision ? "1px solid #D9D2FF" : "1px solid #DCE3EF",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "999px",
                      bgcolor: hasDecision ? "#7A6AD8" : "#6054BB",
                      color: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      fontWeight: 700,
                      fontFamily: "Outfit, sans-serif",
                      flexShrink: 0,
                      mt: 0.1,
                    }}
                  >
                    {childIndex + 1}
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#2C274A",
                        fontFamily: "Outfit, sans-serif",
                        lineHeight: 1.35,
                      }}
                    >
                      {childStep.childStepName?.trim() || "Untitled Child Step"}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.35,
                        fontSize: "11px",
                        color: "#5F6B84",
                        fontFamily: "Outfit, sans-serif",
                        lineHeight: 1.3,
                      }}
                    >
                      {getRoleLabel(childStep.roleId) || "No role selected"}
                    </Typography>

                    <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{ mt: 0.75 }}>
                      {childStep.tatInHours ? (
                        <FlowBadge label={`TAT ${childStep.tatInHours}h`} tone="neutral" />
                      ) : null}
                      {childStep.allowParallelExecution ? (
                        <FlowBadge label="Parallel" tone="primary" />
                      ) : null}
                      {childStep.minimumApprovalCount ? (
                        <FlowBadge
                          label={`Min ${childStep.minimumApprovalCount}`}
                          tone="warning"
                        />
                      ) : null}
                    </Stack>

                    {hasDecision ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.9 }}>
                        <FlowBadge label={`Approve -> Child ${approveSequence}`} tone="success" />
                        <FlowBadge label={`Reject -> Child ${rejectSequence}`} tone="danger" />
                      </Stack>
                    ) : null}
                  </Box>
                </Stack>
              </Box>

              {childIndex < childSteps.length - 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    color: "#AAB7CC",
                    py: 0.2,
                  }}
                >
                  <SouthIcon sx={{ fontSize: 18 }} />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}

function ChildStepCard({ childStep, index, getRoleLabel }) {
  const childRejectTarget =
    childStep.onRejectAction === "gotostep" && childStep.onRejectGoToSequence
      ? `Child Step ${childStep.onRejectGoToSequence}`
      : null;
  const childApproveTarget =
    childStep.onApproveAction === "gotostep" && childStep.onApproveGoToSequence
      ? `Child Step ${childStep.onApproveGoToSequence}`
      : null;

  return (
    <Box
      sx={{
        p: 1.2,
        borderRadius: "3px",
        border: "1px dashed #D7DFEC",
        bgcolor: "#FBFCFE",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography sx={{ ...labelStyle, color: "#72809A" }}>
          Child {index + 1}
        </Typography>
        {childStep.allowParallelExecution && <FlowBadge label="Parallel" tone="primary" />}
        {childStep.minimumApprovalCount ? (
          <FlowBadge label={`Min ${childStep.minimumApprovalCount} approval`} tone="warning" />
        ) : null}
      </Stack>

      <Typography
        sx={{
          mt: 0.8,
          fontSize: "13px",
          fontWeight: 600,
          color: "#2C274A",
          fontFamily: "Outfit, sans-serif",
          lineHeight: 1.35,
        }}
      >
        {childStep.childStepName?.trim() || "Untitled Child Step"}
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          fontSize: "12px",
          color: "#5F6B84",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        {getRoleLabel(childStep.roleId) || "No role selected"}
      </Typography>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
        {childStep.tatInHours ? <FlowBadge label={`TAT ${childStep.tatInHours}h`} tone="neutral" /> : null}
      </Stack>

      {(childApproveTarget || childRejectTarget) && (
        <Stack spacing={0.8} sx={{ mt: 1.2 }}>
          {childApproveTarget && <RoutePill label="Approve" target={childApproveTarget} tone="success" />}
          {childRejectTarget && <RoutePill label="Reject" target={childRejectTarget} tone="danger" />}
        </Stack>
      )}
    </Box>
  );
}

function StepNode({ step, index, getRoleLabel, approveTargetLabel, rejectTargetLabel }) {
  const childSteps = step.childSteps || [];
  const [showChildSteps, setShowChildSteps] = useState(false);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(320px, 420px) minmax(240px, 320px)" },
        gap: 1.5,
        width: "100%",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "3px",
          border: "1px solid #DCE3EF",
          bgcolor: "#FFFFFF",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "999px",
              bgcolor: "#6054BB",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: 700,
              fontFamily: "Outfit, sans-serif",
              flexShrink: 0,
            }}
          >
            {index + 1}
          </Box>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" justifyContent="flex-end">
            {step.useForRejectionStep && <FlowBadge label="Rejection Step" tone="warning" />}
            {childSteps.length > 0 && <FlowBadge label={`${childSteps.length} child steps`} tone="primary" />}
            {!step.isActive && <FlowBadge label="Inactive" tone="danger" />}
          </Stack>
        </Stack>

        <Typography
          sx={{
            mt: 1.2,
            fontSize: "15px",
            fontWeight: 700,
            color: "#2C274A",
            fontFamily: "Outfit, sans-serif",
            lineHeight: 1.35,
          }}
        >
          {step.stepName?.trim() || "Untitled Step"}
        </Typography>

        <Typography
          sx={{
            mt: 0.6,
            fontSize: "12px",
            color: "#5F6B84",
            fontFamily: "Outfit, sans-serif",
          }}
        >
          {getRoleLabel(step.roleId) || "No role selected"}
        </Typography>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.2 }}>
          <FlowBadge label={`Step ${index + 1}`} tone="neutral" />
          {step.tatInHours ? <FlowBadge label={`TAT ${step.tatInHours}h`} tone="neutral" /> : null}
        </Stack>

        {childSteps.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.4,
              borderTop: "1px dashed #D7DFEC",
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setShowChildSteps((value) => !value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setShowChildSteps((value) => !value);
                }
              }}
              sx={{
                p: 1.2,
                borderRadius: "3px",
                border: "1px solid #DCE3EF",
                bgcolor: showChildSteps ? "#F8FAFD" : "#FBFCFE",
                cursor: "pointer",
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <CallSplitIcon sx={{ fontSize: 16, color: "#72809A" }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...labelStyle, color: "#72809A" }}>
                      Child Steps
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        fontSize: "12px",
                        color: "#5F6B84",
                        fontFamily: "Outfit, sans-serif",
                        lineHeight: 1.35,
                      }}
                    >
                      This step contains {childSteps.length} child step
                      {childSteps.length > 1 ? "s" : ""}. Click to {showChildSteps ? "collapse" : "expand"} the child flow.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                  <FlowBadge
                    label={showChildSteps ? "Hide child flow" : "View child flow"}
                    tone="primary"
                  />
                  {showChildSteps ? (
                    <ExpandLessIcon sx={{ fontSize: 20, color: "#5F6B84" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: "#5F6B84" }} />
                  )}
                </Stack>
              </Stack>
            </Box>

            <Collapse in={showChildSteps} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.2 }}>
                <ChildStepsMiniFlow childSteps={childSteps} getRoleLabel={getRoleLabel} />

                <Stack spacing={1}>
                  {childSteps.map((childStep, childIndex) => (
                    <ChildStepCard
                      key={childStep.workflowChildStepId || childIndex}
                      childStep={childStep}
                      index={childIndex}
                      getRoleLabel={getRoleLabel}
                    />
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </Box>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "3px",
          border: "1px dashed #DCE3EF",
          bgcolor: "#FBFCFE",
        }}
      >
        <Typography
          sx={{
            ...labelStyle,
            color: "#72809A",
            mb: 1.1,
          }}
        >
          Outgoing Paths
        </Typography>

        <Stack spacing={1.2}>
          <RoutePill label="Approve" target={approveTargetLabel} tone="success" />
          <RoutePill label="Reject" target={rejectTargetLabel} tone="danger" />
        </Stack>
      </Paper>
    </Box>
  );
}

export default function WorkflowFlowPreview({ roles = [] }) {
  const { values } = useFormikContext();
  const steps = values?.steps || [];

  const roleMap = useMemo(() => {
    return new Map(
      roles.map((role) => [
        String(role.value ?? role.id),
        role.label ?? role.displayName ?? "",
      ])
    );
  }, [roles]);

  const getRoleLabel = useCallback((roleId) => {
    if (roleId == null || roleId === "") return "";
    return roleMap.get(String(roleId)) || "";
  }, [roleMap]);

  const getStepTargetLabel = (sequence) => {
    if (!sequence) return null;
    const targetStep = steps[sequence - 1];
    if (!targetStep) return `Step ${sequence}`;
    return `Step ${sequence} - ${targetStep.stepName?.trim() || "Untitled Step"}`;
  };

  const flowSteps = steps.map((step, index) => {
    const defaultApproveSequence = index < steps.length - 1 ? index + 2 : null;
    const approveSequence =
      step.onApproveAction === "gotostep" && step.onApproveGoToSequence
        ? step.onApproveGoToSequence
        : defaultApproveSequence;
    const rejectSequence =
      step.onRejectAction === "gotostep" && step.onRejectGoToSequence
        ? step.onRejectGoToSequence
        : null;

    return {
      ...step,
      approveTargetLabel: approveSequence
        ? getStepTargetLabel(approveSequence)
        : "End workflow",
      rejectTargetLabel: rejectSequence
        ? getStepTargetLabel(rejectSequence)
        : "Return / stop here",
    };
  });

  const diagram = useMemo(() => {
    const nodeWidth = 340;
    const nodeMinHeight = 84;
    const diamondWidth = 240;
    const diamondMinHeight = 92;
    const startX = 290;
    const startY = 48;
    const gapY = 54;
    const laneOffset = 190;
    const elbowOffset = 22;
    let currentY = startY;
    const nodes = flowSteps.map((step, index) => {
      const approveTarget =
        step.onApproveAction === "gotostep" && step.onApproveGoToSequence
          ? step.onApproveGoToSequence - 1
          : index < flowSteps.length - 1
            ? index + 1
            : null;
      const rejectTarget =
        step.onRejectAction === "gotostep" && step.onRejectGoToSequence
          ? step.onRejectGoToSequence - 1
          : null;
      const hasDecisionBranch =
        approveTarget != null && flowSteps[approveTarget] && rejectTarget != null && flowSteps[rejectTarget];
      const stepNameLines = splitTextIntoLines(
        step.stepName?.trim() || "Untitled Step",
        hasDecisionBranch ? 24 : 42,
        hasDecisionBranch ? 4 : null
      );
      const roleLines = splitTextIntoLines(
        getRoleLabel(step.roleId) || "No role selected",
        hasDecisionBranch ? 24 : 40,
        2
      );
      const width = hasDecisionBranch ? diamondWidth : nodeWidth;
      const titleLineHeight = hasDecisionBranch ? 15 : 16;
      const roleLineHeight = 14;
      const titleY = hasDecisionBranch ? 46 : 28;
      const roleY =
        titleY +
        (stepNameLines.length - 1) * titleLineHeight +
        (hasDecisionBranch ? 30 : 34);
      const childStepsCount = step.childSteps?.length || 0;
      const baseContentHeight = hasDecisionBranch
        ? roleY + Math.max(0, roleLines.length - 1) * roleLineHeight + 18
        : roleY + Math.max(0, roleLines.length - 1) * roleLineHeight + 20;
      const mainHeight = Math.max(
        hasDecisionBranch ? diamondMinHeight : nodeMinHeight,
        baseContentHeight
      );
      const childContainerWidth = childStepsCount
        ? Math.min(hasDecisionBranch ? 400 : Math.max(width + 20, 380), 420)
        : 0;
      const { childLayouts, childRows, childContainerHeight } = buildChildFlowLayouts(step.childSteps);
      const groupHeight = childStepsCount ? mainHeight + 14 + childContainerHeight : mainHeight;
      const x = startX + (nodeWidth - width) / 2;
      const y = currentY;
      currentY += groupHeight + gapY;

      return {
        ...step,
        index,
        x,
        y,
        width,
        height: groupHeight,
        mainHeight,
        shape: hasDecisionBranch ? "diamond" : "rect",
        approveTarget,
        rejectTarget,
        hasDecisionBranch,
        stepNameLines,
        roleLines,
        titleY,
        roleY,
        childStepsCount,
        childContainerWidth,
        childContainerHeight,
        childLayouts,
        childRows,
      };
    });

    const svgWidth = 1140;
    const svgHeight =
      (nodes[nodes.length - 1]?.y || startY) +
      (nodes[nodes.length - 1]?.height || 0) +
      startY +
      90;

    const connectors = [];
    const annotations = [];
    const entryNode = nodes[0]
      ? {
          x: nodes[0].x + nodes[0].width / 2,
          y: 18,
        }
      : null;
    const exitNode = nodes.length
      ? {
          x: nodes[nodes.length - 1].x + nodes[nodes.length - 1].width / 2,
          y: svgHeight - 28,
        }
      : null;

    if (entryNode && nodes[0]) {
      const firstNode = nodes[0];
      connectors.push({
        type: "line",
        tone: "neutral",
        x1: entryNode.x,
        y1: entryNode.y + 12,
        x2: firstNode.x + firstNode.width / 2,
        y2: firstNode.y,
      });
    }

    nodes.forEach((node, index) => {
      const centerX = node.x + node.width / 2;
      const leftX = node.x;
      const rightX = node.x + node.width;
      const bottomY = node.y + node.height;
      const middleY = node.y + node.mainHeight / 2;
      const approveTarget = node.approveTarget;
      const rejectTarget = node.rejectTarget;
      const hasApprovePath = approveTarget != null && nodes[approveTarget];
      const hasRejectPath = rejectTarget != null && nodes[rejectTarget];
      const hasDecisionBranch = node.hasDecisionBranch;

      if (hasApprovePath) {
        const target = nodes[approveTarget];
        if (approveTarget === index + 1) {
          connectors.push({
            type: "line",
            tone: "success",
            x1: centerX,
            y1: bottomY,
            x2: centerX,
            y2: target.y,
          });
          if (hasDecisionBranch) {
            annotations.push({
              text: "Approve",
              tone: "success",
              x: centerX + 18,
              y: bottomY + gapY / 2 - 2,
            });
          }
        } else {
          const laneX = rightX + laneOffset;
          const targetRightX = target.x + target.width;
          const targetMidY = target.y + target.height / 2;
          connectors.push({
            type: "path",
            tone: "success",
            d: buildRoundedOrthogonalPath(
              [
                { x: rightX, y: middleY },
                { x: rightX + elbowOffset, y: middleY },
                { x: laneX, y: middleY },
                { x: laneX, y: targetMidY },
                { x: targetRightX + elbowOffset, y: targetMidY },
                { x: targetRightX, y: targetMidY },
              ],
              12
            ),
          });
          if (hasDecisionBranch) {
            annotations.push({
              text: "Approve",
              tone: "success",
              x: laneX + 12,
              y: approveTarget < index ? targetMidY - 16 : middleY - 16,
            });
          }
        }
      } else if (index === nodes.length - 1 && exitNode) {
        connectors.push({
          type: "line",
          tone: "success",
          x1: centerX,
          y1: bottomY,
          x2: exitNode.x,
          y2: exitNode.y - 12,
        });
      }

      if (hasRejectPath) {
        const target = nodes[rejectTarget];
        const laneX = leftX - laneOffset;
        const targetMidY = target.y + target.height / 2;
        connectors.push({
          type: "path",
          tone: "danger",
          d: buildRoundedOrthogonalPath(
            [
              { x: leftX, y: middleY },
              { x: leftX - elbowOffset, y: middleY },
              { x: laneX, y: middleY },
              { x: laneX, y: targetMidY },
              { x: target.x - elbowOffset, y: targetMidY },
              { x: target.x, y: targetMidY },
            ],
              12
            ),
        });
        if (hasDecisionBranch) {
          annotations.push({
            text: "Reject",
            tone: "danger",
            x: laneX - 70,
            y: rejectTarget < index ? targetMidY - 16 : middleY - 16,
          });
        }
      }
    });

    return { nodes, connectors, annotations, svgWidth, svgHeight, entryNode, exitNode };
  }, [flowSteps, getRoleLabel]);

  if (!steps.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "3px",
          border: "1px dashed #D7DFEC",
          bgcolor: "#FBFCFE",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "#2C274A",
            fontFamily: "Outfit, sans-serif",
          }}
        >
          Workflow Map
        </Typography>
        <Typography
          sx={{
            mt: 1,
            fontSize: "13px",
            color: "#72809A",
            fontFamily: "Outfit, sans-serif",
          }}
        >
          Add steps to see a visual flowchart with approve and reject paths.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "3px",
        border: "1px solid #DCE3EF",
        bgcolor: "#FFFFFF",
      }}
    >
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          color: "#2C274A",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        Workflow Map
      </Typography>

      <Typography
        sx={{
          mt: 0.6,
          fontSize: "13px",
          color: "#72809A",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        Flowchart view of each step, including approve and reject routing.
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
        <FlowBadge label="Approve path" tone="success" />
        <FlowBadge label="Reject path" tone="danger" />
        <FlowBadge label="Child flow inside step" tone="primary" />
        <FlowBadge label="Detailed step cards below" tone="neutral" />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2,
          borderRadius: "3px",
          border: "1px solid #E6EBF5",
          bgcolor: "#FCFDFE",
          overflowX: "auto",
        }}
      >
        <Typography
          sx={{
            ...labelStyle,
            color: "#72809A",
            mb: 1.25,
          }}
        >
          Flowchart
        </Typography>

        <Box sx={{ minWidth: diagram.svgWidth, position: "relative" }}>
          <svg width={diagram.svgWidth} height={diagram.svgHeight}>
            <defs>
              <marker
                id="arrow-success"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="3"
                orient="auto"
              >
                <path d="M0,0 L0,6 L6,3 z" fill="#237B4B" />
              </marker>
              <marker
                id="arrow-danger"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="3"
                orient="auto"
              >
                <path d="M0,0 L0,6 L6,3 z" fill="#D14343" />
              </marker>
            </defs>

            {diagram.connectors.map((connector, index) => {
              const strokeMap = {
                neutral: "#AAB7CC",
                success: "#237B4B",
                danger: "#D14343",
              };
              const markerMap = {
                neutral: "url(#arrow-success)",
                success: "url(#arrow-success)",
                danger: "url(#arrow-danger)",
              };
              const stroke = strokeMap[connector.tone] || strokeMap.success;
              const markerEnd = markerMap[connector.tone] || markerMap.success;

              return connector.type === "line" ? (
                <g key={`connector-${index}`}>
                  <line
                    x1={connector.x1}
                    y1={connector.y1}
                    x2={connector.x2}
                    y2={connector.y2}
                    stroke={stroke}
                    strokeWidth="2"
                    markerEnd={markerEnd}
                  />
                </g>
              ) : (
                <g key={`connector-${index}`}>
                  <path
                    d={connector.d}
                    fill="none"
                    stroke={stroke}
                    strokeWidth="2"
                    markerEnd={markerEnd}
                  />
                </g>
              );
            })}

            {diagram.annotations.map((annotation, index) => {
              const toneMap = {
                success: { fill: "#EAF7EE", text: "#237B4B" },
                danger: { fill: "#FDECEC", text: "#D14343" },
                neutral: { fill: "#F2F5FA", text: "#5F6B84" },
              };
              const colors = toneMap[annotation.tone] || toneMap.neutral;
              const width = Math.max(52, annotation.text.length * 6.3 + 16);

              return (
                <g key={`annotation-${index}`}>
                  <rect
                    x={annotation.x}
                    y={annotation.y - 12}
                    width={width}
                    height="20"
                    rx="10"
                    fill={colors.fill}
                  />
                  <text
                    x={annotation.x + 8}
                    y={annotation.y + 2}
                    fill={colors.text}
                    fontSize="10.5"
                    fontWeight="700"
                    fontFamily="Outfit, sans-serif"
                  >
                    {annotation.text}
                  </text>
                </g>
              );
            })}

            {diagram.entryNode ? (
              <g>
                <ellipse
                  cx={diagram.entryNode.x}
                  cy={diagram.entryNode.y}
                  rx="34"
                  ry="20"
                  fill="#0A65E4"
                />
                <text
                  x={diagram.entryNode.x}
                  y={diagram.entryNode.y + 5}
                  textAnchor="middle"
                  fill="#FFFFFF"
                  fontSize="13"
                  fontWeight="700"
                  fontFamily="Outfit, sans-serif"
                >
                  START
                </text>
              </g>
            ) : null}

            {diagram.exitNode ? (
              <g>
                <ellipse
                  cx={diagram.exitNode.x}
                  cy={diagram.exitNode.y}
                  rx="28"
                  ry="17"
                  fill="#237B4B"
                />
                <text
                  x={diagram.exitNode.x}
                  y={diagram.exitNode.y + 5}
                  textAnchor="middle"
                  fill="#FFFFFF"
                  fontSize="12"
                  fontWeight="700"
                  fontFamily="Outfit, sans-serif"
                >
                  END
                </text>
              </g>
            ) : null}

            {diagram.nodes.map((node) => (
              <g key={`node-${node.index}`}>
                {node.shape === "diamond" ? (
                  <polygon
                    points={[
                      `${node.x + node.width / 2},${node.y}`,
                      `${node.x + node.width},${node.y + node.mainHeight / 2}`,
                      `${node.x + node.width / 2},${node.y + node.mainHeight}`,
                      `${node.x},${node.y + node.mainHeight / 2}`,
                    ].join(" ")}
                    fill="#FFFFFF"
                    stroke="#DCE3EF"
                    strokeWidth="1.5"
                  />
                ) : (
                  <rect
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.mainHeight}
                    rx="14"
                    fill="#FFFFFF"
                    stroke="#DCE3EF"
                    strokeWidth="1.5"
                  />
                )}
                <circle
                  cx={node.x + (node.shape === "diamond" ? node.width / 2 : 20)}
                  cy={node.y + (node.shape === "diamond" ? 18 : 20)}
                  r="12"
                  fill="#6054BB"
                />
                <text
                  x={node.x + (node.shape === "diamond" ? node.width / 2 : 20)}
                  y={node.y + (node.shape === "diamond" ? 22 : 24)}
                  textAnchor="middle"
                  fill="#FFFFFF"
                  fontSize="11"
                  fontWeight="700"
                  fontFamily="Outfit, sans-serif"
                >
                  {node.index + 1}
                </text>
                <text
                  x={node.shape === "diamond" ? node.x + node.width / 2 : node.x + 42}
                  y={node.y + node.titleY}
                  textAnchor={node.shape === "diamond" ? "middle" : "start"}
                  fill="#2C274A"
                  fontSize={node.shape === "diamond" ? "12" : "13"}
                  fontWeight="700"
                  fontFamily="Outfit, sans-serif"
                >
                  {node.stepNameLines.map((line, lineIndex) => (
                    <tspan
                      key={`${node.index}-title-${lineIndex}`}
                      x={node.shape === "diamond" ? node.x + node.width / 2 : node.x + 42}
                      dy={lineIndex === 0 ? 0 : 15}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
                <text
                  x={node.shape === "diamond" ? node.x + node.width / 2 : node.x + 42}
                  y={node.y + node.roleY}
                  textAnchor={node.shape === "diamond" ? "middle" : "start"}
                  fill="#5F6B84"
                  fontSize="11"
                  fontWeight="500"
                  fontFamily="Outfit, sans-serif"
                >
                  {node.roleLines.map((line, lineIndex) => (
                    <tspan
                      key={`${node.index}-role-${lineIndex}`}
                      x={node.shape === "diamond" ? node.x + node.width / 2 : node.x + 42}
                      dy={lineIndex === 0 ? 0 : 14}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
                {node.childStepsCount > 0 ? (
                  <g>
                    <line
                      x1={node.x + node.width / 2}
                      y1={node.y + node.mainHeight}
                      x2={node.x + node.width / 2}
                      y2={node.y + node.mainHeight + 14}
                      stroke="#0A65E4"
                      strokeWidth="1.5"
                    />
                    <rect
                      x={node.x + node.width / 2 - node.childContainerWidth / 2}
                      y={node.y + node.mainHeight + 14}
                      width={node.childContainerWidth}
                      height={node.childContainerHeight}
                      rx="12"
                      fill="#F8FAFD"
                      stroke="#BFD3F6"
                      strokeWidth="1.2"
                      strokeDasharray="5 4"
                    />
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + node.mainHeight + 31}
                      textAnchor="middle"
                      fill="#0A65E4"
                      fontSize="10.5"
                      fontWeight="700"
                      fontFamily="Outfit, sans-serif"
                    >
                      {`Child Flow (${node.childStepsCount})`}
                    </text>
                    <rect
                      x={node.x + node.width / 2 - 52}
                      y={node.y + node.mainHeight + 14 + node.childContainerHeight - 34}
                      width="104"
                      height="18"
                      rx="9"
                      fill="#EAF7EE"
                    />
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + node.mainHeight + 14 + node.childContainerHeight - 22}
                      textAnchor="middle"
                      fill="#237B4B"
                      fontSize="9.5"
                      fontWeight="700"
                      fontFamily="Outfit, sans-serif"
                    >
                      Next Parent Step
                    </text>
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + node.mainHeight + 14 + node.childContainerHeight - 40}
                      textAnchor="middle"
                      fill="#72809A"
                      fontSize="8.5"
                      fontWeight="600"
                      fontFamily="Outfit, sans-serif"
                    >
                      Implicit approve exits here
                    </text>
                    <line
                      x1={node.x + node.width / 2}
                      y1={node.y + node.mainHeight + 14 + node.childContainerHeight - 16}
                      x2={node.x + node.width / 2}
                      y2={node.y + node.mainHeight + 14 + node.childContainerHeight}
                      stroke="#237B4B"
                      strokeWidth="1.5"
                    />
                    {node.childLayouts.map((childNode, childIndex) => {
                      const sourceGeometry = getChildLayoutGeometry(node, childNode);
                      const sourceBottomY = sourceGeometry.childY + childNode.height;

                      if (childNode.approveTargetIndex == null) {
                        return null;
                      }

                      const targetNode = node.childLayouts[childNode.approveTargetIndex];
                      if (!targetNode) return null;

                      const targetGeometry = getChildLayoutGeometry(node, targetNode);
                      const targetTopY = targetGeometry.childY;

                      if (
                        Math.abs(sourceGeometry.childCenterX - targetGeometry.childCenterX) < 2 &&
                        targetTopY >= sourceBottomY
                      ) {
                        return (
                          <line
                            key={`child-approve-${node.index}-${childIndex}`}
                            x1={sourceGeometry.childCenterX}
                            y1={sourceBottomY}
                            x2={targetGeometry.childCenterX}
                            y2={targetTopY}
                            stroke="#237B4B"
                            strokeWidth="1.5"
                            markerEnd="url(#arrow-success)"
                          />
                        );
                      }

                      const mergeY = targetTopY - 10;
                      return (
                        <path
                          key={`child-approve-${node.index}-${childIndex}`}
                          d={buildRoundedOrthogonalPath(
                            [
                              { x: sourceGeometry.childCenterX, y: sourceBottomY },
                              { x: sourceGeometry.childCenterX, y: mergeY },
                              { x: targetGeometry.childCenterX, y: mergeY },
                              { x: targetGeometry.childCenterX, y: targetTopY },
                            ],
                            8
                          )}
                          fill="none"
                          stroke="#237B4B"
                          strokeWidth="1.5"
                          markerEnd="url(#arrow-success)"
                        />
                      );
                    })}
                    {node.childLayouts.map((childNode, childIndex) => {
                      const {
                        childX,
                        childY,
                        childWidth,
                        childCenterX,
                        childShapeWidth,
                        childShapeX,
                        childMidY,
                      } = getChildLayoutGeometry(node, childNode);
                      const leftBranchX = childShapeX;
                      const rejectTargetNode =
                        childNode.rejectTargetIndex != null
                          ? node.childLayouts[childNode.rejectTargetIndex]
                          : null;
                      const rejectTargetGeometry = rejectTargetNode
                        ? getChildLayoutGeometry(node, rejectTargetNode)
                        : null;
                      const rejectTargetY = rejectTargetGeometry ? rejectTargetGeometry.childMidY : null;
                      const rejectTargetX = rejectTargetGeometry ? rejectTargetGeometry.childX : null;
                      const rejectLaneX = childX - 18;

                      return (
                        <g key={`child-node-${node.index}-${childIndex}`}>
                          {childNode.hasDecision ? (
                            <>
                              <circle
                                cx={rejectLaneX}
                                cy={childMidY}
                                r="3.5"
                                fill="#D14343"
                              />
                              <rect
                                x={rejectLaneX - 56}
                                y={childMidY - 24}
                                width="52"
                                height="18"
                                rx="9"
                                fill="#FDECEC"
                              />
                              <text
                                x={rejectLaneX - 30}
                                y={childMidY - 12}
                                textAnchor="middle"
                                fill="#D14343"
                                fontSize="9"
                                fontWeight="700"
                                fontFamily="Outfit, sans-serif"
                              >
                                Reject
                              </text>
                              {rejectTargetY != null ? (
                                <path
                                  d={buildRoundedOrthogonalPath(
                                    [
                                      { x: leftBranchX, y: childMidY },
                                      { x: rejectLaneX + 8, y: childMidY },
                                      { x: rejectLaneX, y: childMidY },
                                      { x: rejectLaneX, y: rejectTargetY },
                                      { x: rejectTargetX - 4, y: rejectTargetY },
                                      { x: rejectTargetX, y: rejectTargetY },
                                    ],
                                    8
                                  )}
                                  fill="none"
                                  stroke="#D14343"
                                  strokeWidth="1.5"
                                />
                              ) : (
                                <line
                                  x1={rejectLaneX}
                                  y1={childMidY}
                                  x2={leftBranchX}
                                  y2={childMidY}
                                  stroke="#D14343"
                                  strokeWidth="1.5"
                                />
                              )}
                              <polygon
                                points={[
                                  `${childShapeX + childShapeWidth / 2},${childY}`,
                                  `${childShapeX + childShapeWidth},${childMidY}`,
                                  `${childShapeX + childShapeWidth / 2},${childY + childNode.height}`,
                                  `${childShapeX},${childMidY}`,
                                ].join(" ")}
                                fill="#FFFFFF"
                                stroke="#D9D2FF"
                                strokeWidth="1.2"
                              />
                            </>
                          ) : (
                            <rect
                              x={childX}
                              y={childY}
                              width={childWidth}
                              height={childNode.height}
                              rx="10"
                              fill="#FFFFFF"
                              stroke="#DCE3EF"
                              strokeWidth="1"
                            />
                          )}
                          <circle
                            cx={childNode.hasDecision ? childCenterX : childX + 14}
                            cy={childNode.hasDecision ? childY + 10 : childY + 14}
                            r="9"
                            fill={childNode.hasDecision ? "#7A6AD8" : "#6054BB"}
                          />
                          <text
                            x={childNode.hasDecision ? childCenterX : childX + 14}
                            y={childNode.hasDecision ? childY + 13 : childY + 17}
                            textAnchor="middle"
                            fill="#FFFFFF"
                            fontSize="8.5"
                            fontWeight="700"
                            fontFamily="Outfit, sans-serif"
                          >
                            {childNode.sequence}
                          </text>
                          <text
                            x={childNode.hasDecision ? childCenterX : childX + 28}
                            y={childY + childNode.titleY}
                            textAnchor={childNode.hasDecision ? "middle" : "start"}
                            fill="#2C274A"
                            fontSize="10.5"
                            fontWeight="700"
                            fontFamily="Outfit, sans-serif"
                          >
                            {childNode.titleLines.map((line, lineIndex) => (
                              <tspan
                                key={`child-title-${node.index}-${childIndex}-${lineIndex}`}
                                x={childNode.hasDecision ? childCenterX : childX + 28}
                                dy={lineIndex === 0 ? 0 : 12}
                              >
                                {line}
                              </tspan>
                            ))}
                          </text>
                          {childNode.approveTargetIndex == null ? (
                            <line
                              x1={childCenterX}
                              y1={childY + childNode.height}
                              x2={childCenterX}
                              y2={childY + childNode.height + 10}
                              stroke="#7DBA94"
                              strokeWidth="1.5"
                              strokeDasharray="4 3"
                            />
                          ) : null}
                        </g>
                      );
                    })}
                  </g>
                ) : null}
                {node.shape === "diamond" ? (
                  <>
                    <circle
                      cx={node.x}
                      cy={node.y + node.mainHeight / 2}
                      r="4"
                      fill="#D14343"
                    />
                  </>
                ) : null}
                <circle
                  cx={node.x + node.width / 2}
                  cy={node.y + node.height}
                  r="4"
                  fill="#237B4B"
                />
              </g>
            ))}
          </svg>
        </Box>
      </Paper>

      <Stack spacing={1.2} sx={{ mt: 2 }}>
        {flowSteps.map((step, index) => (
          <React.Fragment key={step.workflowConfigurationStepId || index}>
            <StepNode
              step={step}
              index={index}
              getRoleLabel={getRoleLabel}
              approveTargetLabel={step.approveTargetLabel}
              rejectTargetLabel={step.rejectTargetLabel}
            />

            {index < flowSteps.length - 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  color: "#AAB7CC",
                }}
              >
                <SouthIcon />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Paper>
  );
}
